import React from "react";
import { useBluelagoon } from "@bluelagoon";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MonitoringHeader from "./MonitoringHeader";
import MonitoringFilters from "./MonitoringFilters";
import ExecutionCardGrid from "./ExecutionCardGrid";
import ExecutionDetailModal from "./ExecutionDetailModal";

const getEffectiveStatus = (exec) => {
    if (exec.status === "running" && (new Date() - new Date(exec.startedAt)) / 60000 > 15) return "pending_finish";
    return exec.status;
};
const formatDuration = (s, c) => {
    if (!s) return "-";
    const ms = (c ? new Date(c) : new Date()) - new Date(s);
    if (ms < 1000) return "< 1s";
    if (ms < 60000) return `${Math.round(ms / 1000)}s`;
    if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
    return `${Math.round(ms / 3600000)}h`;
};
const defaultFrom = () => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
};

function WorkflowExecutionMonitoring() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [triggerType, setTriggerType] = React.useState("all");
    const [workflowId, setWorkflowId] = React.useState("all");
    const [dateFrom, setDateFrom] = React.useState(defaultFrom), [dateTo, setDateTo] = React.useState("");
    const [searchQuery, setSearchQuery] = React.useState(searchParams.get("search") || "");
    const [selectedExecution, setSelectedExecution] = React.useState(null), [isModalOpen, setIsModalOpen] = React.useState(false);
    const [workflows, setWorkflows] = React.useState([]);
    const [refreshKey, setRefreshKey] = React.useState(0);
    const timeoutRef = React.useRef(null);
    const { call: getDetails, isLoading: isLoadingDetails, response: details } = useBluelagoon({ method: "getWorkflowExecutionById" });
    const { call: fetchWorkflowNames } = useBluelagoon({ method: "getWorkflowNames" });

    React.useEffect(() => {
        fetchWorkflowNames().then((res) => {
            if (res?.data?.workflows) setWorkflows(res.data.workflows);
        });
    }, []);

    const filters = React.useMemo(() => {
        const f = {};
        if (searchQuery && searchQuery.trim()) f.search = searchQuery.trim();
        if (triggerType !== "all") f.triggerType = triggerType;
        if (workflowId !== "all") f.workflowId = workflowId;
        if (dateFrom) f.dateFrom = dateFrom;
        if (dateTo) f.dateTo = dateTo;
        f._key = refreshKey;
        return f;
    }, [searchQuery, triggerType, workflowId, dateFrom, dateTo, refreshKey]);

    const handleSearchChange = React.useCallback((value) => {
        setSearchQuery(value);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            const np = new URLSearchParams(searchParams);
            value.trim() ? np.set("search", value.trim()) : np.delete("search");
            setSearchParams(np);
        }, 500);
    }, [searchParams, setSearchParams]);

    const handleViewDetails = async (exec) => {
        setSelectedExecution(exec);
        setIsModalOpen(true);
        await getDetails({ executionId: exec.id, workflowId: exec.workflowId });
    };

    return (
        <div className="flex flex-col gap-3 h-[calc(100vh-100px)] overflow-hidden">
            <MonitoringHeader onRefresh={() => setRefreshKey((k) => k + 1)} isLoading={false} />
            <MonitoringFilters searchQuery={searchQuery} onSearchChange={handleSearchChange} triggerType={triggerType} onTriggerTypeChange={setTriggerType} workflowId={workflowId} onWorkflowIdChange={setWorkflowId} workflows={workflows} dateFrom={dateFrom} onDateFromChange={setDateFrom} dateTo={dateTo} onDateToChange={setDateTo} totalCount={null} isLoading={false} />
            <div className="flex-1 min-h-0">
                <ExecutionCardGrid filters={filters} getEffectiveStatus={getEffectiveStatus} onViewDetails={handleViewDetails} />
            </div>
            <ExecutionDetailModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} execution={selectedExecution} effectiveStatus={selectedExecution ? getEffectiveStatus(selectedExecution) : ""} executionDetails={details} isLoadingDetails={isLoadingDetails} formatDuration={formatDuration} />
        </div>
    );
}

export default WorkflowExecutionMonitoring;
